<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import RequestButton from '@/components/button/RequestButton.vue'

const props = defineProps({
  type: {
    type: String,
    default: 'hero-img',
    validator: (value) =>
      [
        'hero-img',
        'hero-video',
        'hero-img-text-left',
        'hero-img-text-left-button',
        'hero-video-text-left',
        'hero-video-text-left-button',
        'hero-video-text-center',
        'hero-video-text-center-button',
      ].includes(value),
  },
  image: {
    type: String,
    default: null,
  },
  video: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  buttonText: {
    type: String,
    default: 'ОСТАВИТЬ ЗАЯВКУ',
  },
  fallback: {
    type: String,
    default: '/static/placeholders/placeholder-image.webp',
  },
})

const videoRef = ref(null)
const coverRef = ref(null)
const isLoaded = ref(false)
const isCoverLoaded = ref(false)
const isFiveSecLoaded = ref(false)
const isTimeout = ref(false)
const videoSrc = ref('')
const coverSrc = ref('')
const videoError = ref(false)
const coverError = ref(false)

async function loadAndCacheVideo() {
  if (!props.video) return
  const cache = await caches.open('video-cache')
  const cachedResponse = await cache.match(props.video)
  const response = cachedResponse || (await fetch(props.video))
  if (!cachedResponse) {
    cache.put(props.video, response.clone())
  }
  const blob = await response.blob()
  videoSrc.value = URL.createObjectURL(blob)
}

async function loadAndCacheCover() {
  if (!props.image) return
  const cache = await caches.open('cover-cache')
  const cachedResponse = await cache.match(props.image)
  const response = cachedResponse || (await fetch(props.image))
  if (!cachedResponse) {
    cache.put(props.image, response.clone())
  }
  const blob = await response.blob()
  coverSrc.value = URL.createObjectURL(blob)
}

const onCoverLoad = () => {
  isCoverLoaded.value = true
}

const getContentClass = () => {
  if (props.type.includes('text-left')) {
    return 'hero-img-text-left' + (props.type.includes('button') ? '-button' : '')
  } else if (props.type.includes('text-center')) {
    return 'hero-video-text-center' + (props.type.includes('button') ? '-button' : '')
  }
  return ''
}

const handleVideoProgress = () => {
  if (!videoRef.value) return
  const buffered = videoRef.value.buffered
  if (buffered.length > 0) {
    const fiveSec = Math.min(5, videoRef.value.duration || 5)
    if (buffered.end(0) >= fiveSec) {
      isFiveSecLoaded.value = true
    }
  }
}

const handleVideoLoaded = () => {
  isLoaded.value = true
}

const handleVideoError = () => {
  console.warn('Video failed to load:', props.video)
  videoError.value = true
}

const handleCoverError = () => {
  console.warn('Cover image failed to load:', props.image)
  coverError.value = true
}

const handleImageError = (event) => {
  const img = event.target
  img.onerror = null
  img.src = props.fallback
}

onMounted(async () => {
  setTimeout(() => {
    isTimeout.value = true
  }, 3000)

  if (props.video) {
    try {
      await loadAndCacheVideo()
      if (videoRef.value) {
        videoRef.value.src = videoSrc.value
        videoRef.value.addEventListener('loadeddata', handleVideoLoaded)
        videoRef.value.addEventListener('progress', handleVideoProgress)
        videoRef.value.addEventListener('error', handleVideoError)
      }
    } catch (error) {
      console.warn('Failed to load video:', error)
      videoError.value = true
    }
  }

  if (props.image) {
    try {
      await loadAndCacheCover()
    } catch (error) {
      console.warn('Failed to load cover:', error)
      coverError.value = true
    }
  }
})

onUnmounted(() => {
  if (videoRef.value) {
    videoRef.value.removeEventListener('loadeddata', handleVideoLoaded)
    videoRef.value.removeEventListener('progress', handleVideoProgress)
    videoRef.value.removeEventListener('error', handleVideoError)
  }
})
</script>

<template>
  <div class="hero-block" :class="[props.type]">
    <!-- Hero Media Section -->
    <div class="hero-media">
      <!-- Skeleton Loader -->
      <div v-if="props.image && !isCoverLoaded && !isTimeout" class="hero-skeleton"></div>

      <!-- Image -->
      <div v-if="props.image && !props.video" class="hero-image">
        <img
          :src="coverSrc || props.image"
          :alt="props.title || ''"
          loading="lazy"
          decoding="async"
          @load="onCoverLoad"
          @error="handleImageError"
        />
      </div>

      <!-- Fallback if no image or video -->
      <div v-if="!props.image && !props.video" class="hero-fallback">
        <div class="fallback-placeholder">
          <span>No media content</span>
        </div>
      </div>

      <!-- Video -->
      <div v-if="props.video" class="hero-video">
        <!-- Show fallback image if video failed to load -->
        <div v-if="videoError" class="hero-video-fallback">
          <img :src="props.fallback" :alt="props.title || ''" loading="lazy" decoding="async" />
        </div>
        <!-- Show video if it loaded successfully -->
        <video
          v-else
          ref="videoRef"
          :src="videoSrc"
          muted
          autoplay
          loop
          playsinline
          preload="metadata"
          class="hero-video-element"
        />
        <!-- Video Cover -->
        <div v-if="props.image && !videoError" class="hero-video-cover" ref="coverRef">
          <img
            :src="coverSrc || props.image"
            :alt="props.title || ''"
            loading="lazy"
            decoding="async"
            @load="onCoverLoad"
            @error="handleCoverError"
          />
        </div>
        <!-- Cover fallback if cover failed to load -->
        <div v-if="props.image && !videoError && coverError" class="hero-video-cover">
          <img :src="props.fallback" :alt="props.title || ''" loading="lazy" decoding="async" />
        </div>
      </div>
    </div>

    <!-- Text Content Section -->
    <div v-if="props.title || props.description" class="hero-content" :class="getContentClass()">
      <!-- White gradient overlay for better text readability -->
      <div class="hero-gradient-overlay"></div>
      <div class="hero-text-wrapper">
        <h1 v-if="props.title" class="hero-title">{{ props.title }}</h1>
        <p v-if="props.description" class="hero-description">{{ props.description }}</p>
        <RequestButton v-if="props.buttonText" :text="props.buttonText" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.hero-block {
  position: relative;
  width: 100%;
  height: calc(100vh - 5rem);
  margin-left: 50%;
  transform: translateX(-50%);
  overflow: hidden;
  max-width: 100%;
}

.hero-media {
  position: relative;
  width: 100%;
  height: 100%;
}

.hero-skeleton {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-skeleton);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.2s infinite linear;
  z-index: 4;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.hero-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.hero-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.hero-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.hero-video-element {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.hero-video-fallback {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.hero-video-fallback img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.hero-video-cover {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 1;
  transition: opacity 0.8s ease;
  z-index: 2;
}

.hero-video-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.hero-content {
  position: absolute;
  z-index: 3;
  padding-bottom: 2rem;
}

.hero-gradient-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 50dvh;
  background: linear-gradient(
    to top,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(255, 255, 255, 0.8) 10%,
    rgba(255, 255, 255, 0.4) 40%,
    rgba(255, 255, 255, 0) 90%
  );
  pointer-events: none;
  z-index: 1;
}

.hero-content.hero-img-text-left,
.hero-content.hero-img-text-left-button {
  bottom: 0;
  left: 0;
  width: 100%;
  text-align: left;
}

.hero-content.hero-video-text-center,
.hero-content.hero-video-text-center-button {
  bottom: 0;
  left: 0;
  width: 100%;
  text-align: center;
}

.hero-text-wrapper {
  display: flex;
  flex-direction: column;
  max-width: 90rem;
  width: 90%;
  margin: 0 auto;
  position: relative;
  z-index: 2;
}

.hero-text-wrapper .application-button {
  align-self: flex-start;
}

/* Кнопка по левому краю для text-left типов */
.hero-content.hero-img-text-left .hero-text-wrapper .application-button,
.hero-content.hero-img-text-left-button .hero-text-wrapper .application-button,
.hero-content.hero-video-text-left .hero-text-wrapper .application-button,
.hero-content.hero-video-text-left-button .hero-text-wrapper .application-button {
  align-self: flex-start;
  margin-left: 0;
}

/* Кнопка по центру для text-center типов */
.hero-content.hero-video-text-center .hero-text-wrapper .application-button,
.hero-content.hero-video-text-center-button .hero-text-wrapper .application-button {
  align-self: center;
}

.hero-title {
  color: var(--color-text-primary);
  margin-bottom: 1rem;
  font-family: Montserrat, serif;
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  letter-spacing: 0.06rem;
}

.hero-description {
  font-size: 1.125rem;
  color: var(--color-text-primary);
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.hero-fallback {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-secondary);
}

.fallback-placeholder {
  padding: 2rem;
  text-align: center;
  color: var(--color-text-muted);
  font-size: 1.2rem;
}

@media (max-width: 48rem) {
  .hero-title {
    font-size: 1.5rem;
  }

  .hero-description {
    font-size: 1rem;
  }

  .hero-content {
    padding: 1.5rem 0;
  }

  .hero-gradient-overlay {
    height: 60dvh;
  }

  .hero-text-wrapper {
    margin: 0 auto;
  }
}
</style>
